import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { ApiError, unwrap } from '@/api/envelope';
import { getOrganization, updateOrganization } from '@/api/generated';
import type { OrganizationResponse } from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { CollapsibleCard } from '@/components/ui/CollapsibleCard';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardSkeleton, SectionSkeleton } from '@/components/ui/Skeletons';
import { useToast } from '@/components/ui/useToast';

import { RecentActivity } from './RecentActivity';
import { useViewPortal } from './useViewPortal';
import { SuperAdminsCard } from './SuperAdminsCard';
import { ChangeStatusDialog } from './ChangeStatusDialog';
import { BillingForm } from './BillingForm';
import { NotificationAllowanceCard } from './NotificationAllowanceCard';
import { storeSavedOrganization } from './organizationCache';
import { OrganizationForm, type OrganizationFormHandle } from './OrganizationForm';
import { STATUS_COLOUR, STATUS_LABELS, formatDate } from '@/lib/organization';
import { isFieldDirty } from '@/lib/dirtyFields';

import {
  isHandleConflict,
  toFormValues,
  toUpdatePayload,
  type OrganizationDirtyFields,
  type OrganizationFormOutput,
} from './organizationSchema';

/** A save that has been asked for and is waiting on an answer. */
interface PendingSave {
  values: OrganizationFormOutput;
  dirtyFields: OrganizationDirtyFields;
}

function statusLabel(status: string) {
  return STATUS_LABELS[status] ?? status;
}

/** The name, handle, status and age of the account, above everything else. */
function AccountHeader({ record }: { record: OrganizationResponse }) {
  const viewPortal = useViewPortal();

  return (
    <>
      {/* Also sets the browser tab, so a row of open accounts can be told
          apart. */}
      <PageHeader
        title={record.name}
        action={
          // What the organization's own people see, read-only - for when a
          // Super Admin rings to say a page will not do what they expect.
          <Button
            variant="secondary"
            onClick={() => viewPortal({ id: record.id, name: record.name })}
            data-testid="org-account-view-portal-button"
          >
            View portal
          </Button>
        }
      />

      {/* Stacked on a phone rather than wrapped: wrapped, the line was one
          row or two depending on the handle's letters, and the loading
          placeholder could not match it. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1.5,
          mt: -2,
          mb: 3,
        }}
      >
        <Chip
          label={statusLabel(record.status)}
          color={STATUS_COLOUR[record.status] ?? 'default'}
          size="small"
          data-testid="org-account-header-status-chip"
        />
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="org-account-slug-value"
        >
          /{record.slug}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="org-account-created-value"
        >
          Created {formatDate(record.created_at)}
        </Typography>
      </Box>
    </>
  );
}

/** The side column's first card, and the way into changing the status. */
function AccountCard({
  record,
  onChangeStatus,
}: {
  record: OrganizationResponse;
  onChangeStatus: () => void;
}) {
  return (
    <Paper variant="outlined" data-testid="org-account-card" sx={{ p: 3 }}>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
        Account
      </Typography>

      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: 1.5,
          columnGap: 2,
          alignItems: 'center',
          m: 0,
        }}
      >
        <Typography component="dt" variant="body2" color="text.secondary">
          Status
        </Typography>
        <Box component="dd" sx={{ m: 0 }}>
          <Chip
            label={statusLabel(record.status)}
            color={STATUS_COLOUR[record.status] ?? 'default'}
            size="small"
            data-testid="org-account-card-status-chip"
          />
        </Box>

        <Typography component="dt" variant="body2" color="text.secondary">
          Status changed
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          data-testid="org-account-card-status-changed-value"
          sx={{ m: 0 }}
        >
          {record.status_changed_at
            ? formatDate(record.status_changed_at)
            : 'Not changed since creation'}
        </Typography>

        {/* Nothing at all when no reason was given. An empty row reads as a
            reason that was lost rather than one never asked for. */}
        {record.status_reason ? (
          <>
            <Typography component="dt" variant="body2" color="text.secondary">
              Reason
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              data-testid="org-account-card-reason-value"
              sx={{ m: 0 }}
            >
              {record.status_reason}
            </Typography>
          </>
        ) : null}

        {record.activated_at ? (
          <>
            <Typography component="dt" variant="body2" color="text.secondary">
              First went live
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              data-testid="org-account-card-activated-value"
              sx={{ m: 0 }}
            >
              {formatDate(record.activated_at)}
            </Typography>
          </>
        ) : null}

        <Typography component="dt" variant="body2" color="text.secondary">
          Created
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          data-testid="org-account-card-created-value"
          sx={{ m: 0 }}
        >
          {formatDate(record.created_at)}
        </Typography>
      </Box>

      <Button
        variant="secondary"
        size="sm"
        sx={{ mt: 3 }}
        onClick={onChangeStatus}
        data-testid="org-account-card-change-status-button"
      >
        Change status
      </Button>
    </Paper>
  );
}

/** A way back to the list, offered wherever the account itself is not there. */
function BackToList() {
  return (
    <Button
      component={RouterLink}
      to={ROUTES.staff.organizations}
      variant="secondary"
      data-testid="org-account-back-link"
    >
      Back to organizations
    </Button>
  );
}

/**
 * One organization's account: what it is, and the form that changes it.
 *
 * Laid out as two columns: the details form in the main one, and in the side
 * one the account card, the way into changing the status, and what has happened
 * to the account. Billing and the Super Admin card extend the same column
 * rather than replacing it.
 */
export function OrganizationPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();

  const form = useRef<OrganizationFormHandle>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const [formError, setFormError] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);

  const query = useQuery({
    queryKey: ['staff', 'organizations', id],
    queryFn: async () => {
      const reply = await getOrganization({
        path: { organization_id: id },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
  });

  /*
   * Bring a failure into view, the same way the create page does. The form is
   * long, and a message at the top of it is out of sight from the Save button.
   * Focus goes with the scroll, so it is announced rather than only seen.
   */
  useEffect(() => {
    if (!formError) return;
    alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    alertRef.current?.focus();
  }, [formError]);

  const save = useMutation({
    mutationFn: async ({ values, dirtyFields }: PendingSave) => {
      const reply = await updateOrganization({
        path: { organization_id: id },
        // The record the form was filled from, whose contact address the form
        // does not show but must not lose.
        body: toUpdatePayload(values, dirtyFields, {
          primary_contact: query.data?.primary_contact,
        }),
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => {
      setFormError(null);
    },
    onSuccess: async (record) => {
      // What the server stored, not what was typed. They are usually the same;
      // when they are not, the server is right.
      form.current?.reset(toFormValues(record));
      // The header reads the cached record, so a rename has to be written back
      // here. Waiting for the refetch below would leave the old name at the top
      // of the page for as long as the round trip takes.
      storeSavedOrganization(queryClient, record);
      toast.show({ message: `${record.name} saved.` });
      // By prefix, so the list, its filters and its counts all go with it.
      await queryClient.invalidateQueries({ queryKey: ['staff', 'organizations'] });
    },
    onError: (error) => {
      // A handle clash is the one 409 with no field to sit under - the handle
      // box is real, but the message names links and history, so it reads as
      // a form-level warning. Everything else goes through the form's own
      // mapping: a name clash onto Name, field messages onto their fields,
      // the rest back here for the alert.
      if (error instanceof ApiError && error.status === 409) {
        const message = error.serverMessage ?? error.message;
        if (isHandleConflict(message)) {
          setFormError(message);
          return;
        }
      }
      const { remainder } = form.current?.applyServerErrors(error) ?? {
        remainder: 'The organization could not be saved. Please try again.',
      };
      if (remainder) setFormError(remainder);
    },
  });

  function submit(values: OrganizationFormOutput, dirtyFields: OrganizationDirtyFields) {
    // Changing the handle breaks links that already exist, so the person about
    // to do it is asked first rather than told afterwards.
    if (isFieldDirty(dirtyFields, 'slug')) {
      setPendingSave({ values, dirtyFields });
      return;
    }
    save.mutate({ values, dirtyFields });
  }

  if (query.isPending) {
    return (
      <Box data-testid="org-account-page">
        {/* Shapes, not a form: a form full of empty boxes reads as an account
            with nothing in it. The same column the page will have - the header
            with its View portal button, the status line under the name, the
            account card, the open Details section and the four folded ones -
            each as tall as it is once loaded, on a phone and on a desktop, so
            nothing jumps when the account arrives. A name that takes two lines
            on a phone adds one line, which is within what the browser tests
            allow. */}
        <PageHeader
          title="Organization"
          action={<Skeleton variant="rounded" width={110} height={37} />}
        />
        {/* The status line: three stacked rows on a phone, one row above. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: { xs: 88, sm: 24 },
            mt: -2,
            mb: 3,
          }}
        >
          <Skeleton width={220} />
        </Box>
        <Stack spacing={2} data-testid="org-account-loading" sx={{ maxWidth: 1000 }}>
          <CardSkeleton
            lines={3}
            label="Loading this organization..."
            minHeight={{ xs: 259, md: 239 }}
          />
          <SectionSkeleton fields={10} columns={2} minHeight={{ xs: 1630, md: 1510 }} />
          <CardSkeleton lines={0} minHeight={{ xs: 102, md: 82 }} />
          <CardSkeleton lines={0} minHeight={{ xs: 102, md: 82 }} />
          <CardSkeleton lines={0} minHeight={{ xs: 122, md: 82 }} />
          <CardSkeleton lines={0} minHeight={{ xs: 102, md: 82 }} />
        </Stack>
      </Box>
    );
  }

  if (query.isError) {
    // An address that is no organization ID at all is refused as malformed
    // (422) before anything is looked up. It is as missing as an unknown one.
    const missing =
      query.error instanceof ApiError &&
      (query.error.status === 404 || query.error.status === 422);

    return (
      <Box data-testid="org-account-page" sx={{ maxWidth: 560 }}>
        <PageHeader title={missing ? 'Organization not found' : 'Organization'} />
        {/* Nothing to retry when the account is not there: the same request
            would give the same answer. The way back is offered either way. */}
        <ErrorState
          title={
            missing
              ? 'This organization was not found. It may have been removed, or the address may be wrong.'
              : 'This organization could not be loaded.'
          }
          onRetry={missing ? undefined : () => void query.refetch()}
          action={<BackToList />}
          testId="org-account"
        />
      </Box>
    );
  }

  const record = query.data;

  return (
    <Box data-testid="org-account-page" sx={{ maxWidth: 1000 }}>
      <AccountHeader record={record} />

      {/*
        One column of folding cards rather than two columns of open ones.

        The page used to put the forms in one column and the account, the
        Super Admins and the activity in a 320 pixel column beside them, with
        everything open at once - several screens of it, and the billing form
        alone is twenty fields. Worse, a grid column will not shrink a child
        below the width that child insists on, so one long email address in
        the Super Admins card grew that column past its 320 pixels and pushed
        the whole page sideways.

        Stacked and folded, each section gets the full width, the page opens at
        a length somebody can take in, and there is no narrow column left to
        blow out. The bodies stay mounted when folded, so a half-typed form is
        still there when the section is opened again.
      */}
      <Stack spacing={2}>
        <AccountCard record={record} onChangeStatus={() => setChangingStatus(true)} />

        <CollapsibleCard
          title="Details"
          testId="org-account-details-card"
          description="Name, handle, type, where they work and who to contact."
          // Open to begin with: it is what the page is for, and the only
          // section somebody arriving here is certain to want.
          defaultExpanded
          // Forced open when a save fails, because the message is inside it
          // and an error nobody can see is not an error report.
          expanded={formError ? true : undefined}
        >
          {formError ? (
            <Alert
              ref={alertRef}
              severity="error"
              role="alert"
              tabIndex={-1}
              data-testid="org-account-details-alert"
              sx={{ mb: 3, maxWidth: 760, '&:focus': { outline: 'none' } }}
            >
              {formError}
            </Alert>
          ) : null}

          <OrganizationForm
            ref={form}
            /* Keyed on the record: the route reuses this page when the global
               search opens another organization, and a form reads its default
               values only once. Without the key the previous organization's
               values stayed in the fields under the new one's name. */
            key={record.id}
            mode="edit"
            embedded
            defaultValues={toFormValues(record)}
            onSubmit={submit}
            pending={save.isPending}
            submitLabel="Save changes"
          />
        </CollapsibleCard>

        {/* Its own form and its own Save. Each one sends only its own slice of
            the record, so a slip in billing is never saved alongside an
            unrelated edit to the profile. */}
        <CollapsibleCard
          title="Billing"
          testId="org-account-billing-card"
          description="Recorded here, invoiced outside the platform. No payment is taken."
        >
          <BillingForm key={record.id} organizationId={id} record={record} embedded />
        </CollapsibleCard>

        <CollapsibleCard
          title="Notification allowance"
          testId="org-account-allowance-card"
          description="How many push notifications the organization may send each month."
        >
          <NotificationAllowanceCard key={record.id} record={record} />
        </CollapsibleCard>

        {/* Who can administer this organization. It fetches its own list and
            shows its own failure, so a portal-users outage leaves the rest of
            the account readable. */}
        <CollapsibleCard
          title="Super Admins"
          testId="org-account-super-admins-card"
          description="Who can administer this organization, and the invitations sent."
        >
          <SuperAdminsCard organizationId={id} embedded />
        </CollapsibleCard>

        <CollapsibleCard
          title="Recent activity"
          testId="org-account-activity-card"
          description="What has happened to this account."
        >
          <RecentActivity record={record} />
        </CollapsibleCard>
      </Stack>

      <ChangeStatusDialog
        organization={{ id: record.id, name: record.name, status: record.status }}
        open={changingStatus}
        onClose={() => setChangingStatus(false)}
      />

      <ConfirmDialog
        open={pendingSave !== null}
        testId="org-account-handle-dialog"
        title="Change the web handle?"
        description="Links that use the old handle will stop working. Change it anyway?"
        confirmLabel="Change the handle"
        pending={save.isPending}
        onConfirm={() => {
          if (pendingSave) save.mutate(pendingSave);
          setPendingSave(null);
        }}
        // Declining leaves the form exactly as it was: still dirty, nothing sent.
        onCancel={() => setPendingSave(null)}
      />
    </Box>
  );
}
